import {
  LAST_PAGING_SESSION_STORAGE_KEY,
  SESSION_STORAGE_KEY
} from '../2022/car-search';
import {
  convertUrlSearchParams,
  elementIsCenterOfScreen,
  formatAjaxBody,
  handleCheckboxAndRadio,
  isCheckboxOrRadioFilter,
  toggleSearchActive
} from './car-search-helper';

/**
 * Adds functionality to car search
 */
class CarSearchIncoming {

  /**
   * Car search instance itself
   *
   * @type {instance|boolean}
   * @static
   */
  static instance = false;

  /**
   * Car search container
   * @type {Element}
   * @private
  */
  element;

  /**
   * If results loaded from sessionStorage this will be true
   * to indicate popstate event not to fire search function
   *
   * @type {boolean}
   * @private
   */
  #sessionStorageLoaded = false;

  /**
   * Element querySelector search strings
   * Used when constructed, loop and fill elementPartials
   *
   * @type {object}
   * @private
   */
  #elementPartialsQueryStrings = {
    loadMore: '#car-search-load-more',
    changeOrder: '.car-search-main__results-order-dropdown',
    resultsCount: '.car-search-main__results-count-container#results-count',
    carsGrid: '.container.cars',
    noMoreResults: '#car-search-no-more-results',
    loadMoreSpinner: '.car-search-main__loader-holder',
  }

  /**
   * Maximum pages load with infinite scroll
   * until the "load more" button will set visible
   *
   * @type {number}
   * @private
   */
  #maxInfiniteLoadPages = 2;

  /**
   * Element querySelectorAll search strings
   * Used when constructed, loop and fill elementPartials
   *
   * @type {object}
   * @private
   */
  #multipleElementPartialsQueryStrings = {
    carCountElements: '[data-car-count]',
  }

  /**
   * Different kind of element partials inside
   * Car Search, such as "Load more" -buttons etc.
   *
   * @type {object}
   * @private
   */
  #elementPartials = {}

  /**
   * Inputs contains all inputs available in car search
   *
   * @type {HTMLElement[]}
   * @private
   */
  #inputs = [];

  /**
   * Different types of cards data
   *
   * @type {object}
   * @private
   */
  #cards = {
    incomingCard: JSON.parse(document.getElementById('incoming-card-json').innerHTML),
    liftCard: JSON.parse(document.getElementById('lift-card-json').innerHTML),
  }

  /**
   * Currently visible car cards length
   */
  #visibleCarsCount = 0;

  /**
   * Current cars count. Used when comparing cars count
   * and if we need to load more or not.
   *
   * @type {number}
   * @private
   */
  #currentCarsCount = 0;

  /**
   * Current page for loadMore
   *
   * @type {number}
   * @private
   */
  #currentPage = 0;

  /**
   * Last loader state saved here
   * When scrolling and e.g. loader position is fixed
   * save fixed here so we do not have to set same values
   * to loader element
   *
   * @type {string} - Available values: top, bottom, fixed
   */
  #lastLoaderState = 'top';

  /**
   * Store first and last cards elements here
   * This will avoid querySelectors inside scroll -event
   *
   * @type {object}
   * @private
   */
  #firstAndLastCardElements = {
    first: null,
    last: null
  }

  /**
   * Defines if search app is currently loading new cars
   *
   * @type {boolean}
   * @private
   */
  #isLoading = false;

  /**
   * Abort controller to handle ajax-requests aborts
   *
   * @type {AbortController|boolean} - Default is false
   */
  abortController = false

  /**
   * Constructor. Starts everything up if accordion elements are found
   * @param {HTMLElement} element element to bind accordion item functionality
   * @returns {CarSearchIncoming} CarSearchIncoming singleton instance
   */
  constructor(element) {
    if (CarSearchIncoming.instance) {
      // Return singleton reference if it exists already
      return CarSearchIncoming.instance;
    }

    // Store singleton reference for future instances.
    CarSearchIncoming.instance = this;
    this.element = element;

    // Fill elementPartials member
    for (const [elementName, queryString] of Object.entries(this.#elementPartialsQueryStrings)) {
      this.#elementPartials[elementName] = this.element.querySelector(queryString);
    }

    // Fill elementPartials member
    for (const [elementName, queryString] of Object.entries(this.#multipleElementPartialsQueryStrings)) {
      this.#elementPartials[elementName] = this.element.querySelectorAll(queryString);
    }

    // Get current cars count, parse window.currentPaging
    // window.currentPaging is printed straight to the dom
    this.#currentCarsCount = +JSON.parse(window.currentPaging).totalResults;

    this.#visibleCarsCount = this.#elementPartials.carsGrid.querySelectorAll('.card--incoming-card').length;

    // Get inputs effective to car search
    this.#inputs = document.querySelectorAll('.car-search-effective');

    // Bind element events
    this.#bindEvents();

    const selectedFilters = this.getSelectedFilters();

    // Handle sessionStorage results if exists
    this.#handleResultsFromSessionStorage();

    this.#observeLoadMoreButton();

    // Save initial history state
    history.pushState(selectedFilters, '');

    this.#handleNoMoreResultsLeft();

    // Set first and last cards position info
    this.#setFirstAndLastCardsPositionInfo();
    // Set loader initial top value
    this.#setLoaderInitialTopValue();
  }

  /**
   * Search incoming cars
   * @param {object}  options    - Options object
   */
  async searchCars(options = {}) {
    this.toggleLoadingAnimation = true;

    // Force calculate position for spinner
    this.#calculateLoadingSpinnerPosition(true);

    // Abort possible last request
    this.#abortSearch();
    this.abortController = new AbortController();

    // If load more option does not exists
    // reset current page, because GET-parameters
    // has changed.
    if (!options.loadMore) {
      this.#currentPage = 0;
    }

    const filters = this.getSelectedFilters();

    if (Array.isArray(filters)) {
      const formattedAjaxBody = formatAjaxBody(filters, window.currentBrand);
      const ajaxBody = convertUrlSearchParams(formattedAjaxBody.ajaxBody, false, this.#currentPage);

      // User has changed filters, scroll smoothly to top
      if (!options.doNotSaveHistory) {
        window.scroll({
          top: 0,
          behavior: 'smooth'
        });

        this.#updateURL(convertUrlSearchParams(formattedAjaxBody.filteredAjaxBody, true, this.#currentPage), filters);
      }

      try {
        await fetch(`/stage-ajax/2022-car-search/?${ajaxBody}`, {
          signal: this.abortController.signal
        })
          .then(response => response.json())
          .then(response => {
            this.toggleLoadingAnimation = false;

            this.#renderData(response.vehicles, response.lifts, options.loadMore);

            // When not loading more, update URL and set load more button invsible
            if (!options.doNotSaveHistory) {
              this.#elementPartials.loadMore.classList.remove('car-search-main__load-more-button--in');
            }

            // Update results count
            for (const carCountElement of this.#elementPartials.carCountElements) {
              carCountElement.dataset.carCount = response.paging.totalResults;
            }

            // Save only GT-X cars count to avoid load nothing when
            // search results ends
            this.#currentCarsCount = response.paging.totalResults;

            // Update page title
            document.title = response.pageTitle;

            // Calculate visible cars count
            this.#visibleCarsCount = this.#elementPartials.carsGrid.querySelectorAll('.card--incoming-card').length;
            this.#handleNoMoreResultsLeft();

            // Get first and last cards top and scrollheight
            this.#setFirstAndLastCardsPositionInfo();
          }).catch(() => { });
      } catch (e) {        
        // eslint-disable-next-line indent
      }
    }
  }

  /**
   * Get selected filters from DOM and return
   *
   * @returns {object[]}
   */
  getSelectedFilters() {
    return Array.from(this.#inputs).flatMap(filter => {
      let returnValue = {};
      // If checkbox or radio, add this to returnValue
      if (isCheckboxOrRadioFilter(filter)) {
        returnValue = handleCheckboxAndRadio(filter);
      }

      if (Object.keys(returnValue).length && filter.dataset.noTag) {
        returnValue.noTag = true;
      }

      return returnValue;
    }).filter(o => Object.keys(o).length);
  }

  /**
   * Set loading state, if loading set loader
   * visible and fade search container
   *
   * @param {boolean} value - True if loading, otherwise false
   */
  set toggleLoadingAnimation(value) {
    toggleSearchActive(value);
    this.#isLoading = value;
  }

  /**
   * Render data
   * @param {object[]} incomingCars - Vehicles array from server
   * @param {object[]} liftCards - Lift cards array from server { title: string, description: string, image: string
   * @param {boolean} append Append vehicles
   */
  #renderData(incomingCars, liftCards, append = false) {
    const carsMarkup = incomingCars.map(car => {
      const customClasses = this.#cards.incomingCard.cardCustomClasses;
      return window.Dali.render(this.#cards.incomingCard.template, {
        ...car,
        unlinked: true,
        cardCustomClasses: customClasses,
      }, this.#cards.incomingCard.partials);
    });

    liftCards.forEach((lift, index) => {
      const liftMarkup = window.Dali.render(this.#cards.liftCard.template, {
        ...lift,
        cardCustomClasses: this.#cards.liftCard.cardCustomClasses
      }, this.#cards.liftCard.partials);

      const indexToInject = (index + 1) * 3; // 3, 6, 9 etc.

      carsMarkup.splice(indexToInject, 0, liftMarkup);
    })

    if (!append) {
      document.querySelector('.car-search-grid').innerHTML = carsMarkup.join('');
    } else {
      document.querySelector('.car-search-grid').innerHTML += carsMarkup.join('');
    }
  }

  /**
   * Aborts current search if search is running
   */
  #abortSearch() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Bind events to necessary inputs
   *
   * @private
   */
  #bindEvents() {
    // Listen tertiary event
    this.#elementPartials.changeOrder.addEventListener('tertiaryChange', e => {
      this.searchCars();
    });

    // Load more event
    this.#elementPartials.loadMore.addEventListener('click', this.#loadMore.bind(this));

    window.addEventListener('popstate', this.#handlePopState.bind(this));

    // Scroll & resize events
    ['scroll', 'resize'].forEach(eventListenerType => window.addEventListener(eventListenerType, () =>
      this.#calculateLoadingSpinnerPosition(eventListenerType === 'resize'),
    { passive: true })
    );
  }

  /**
   * Calculate spinner position in viewport
   *
   * @param {boolean} forceCalculate - Ignore "isLoading" and "lastLoaderState"
   * @private
   */
  #calculateLoadingSpinnerPosition(forceCalculate = false) {
    if (this.#isLoading || forceCalculate) {
      const firstCardOverHalf = elementIsCenterOfScreen(this.#firstAndLastCardElements.first);
      const lastCardOverHalf = elementIsCenterOfScreen(this.#firstAndLastCardElements.last);

      // First card and last card has not crossed center of screen --> top
      if (!firstCardOverHalf && !lastCardOverHalf && (this.#lastLoaderState !== 'top' || forceCalculate)) {
        this.#lastLoaderState = 'top';
        this.#elementPartials.loadMoreSpinner.classList.remove('car-search-main__loader-holder--fixed');
        // Get offset top value from
        this.#setLoaderInitialTopValue();
      }

      // First card has crossed center of screen --> scrolling
      if (firstCardOverHalf && !lastCardOverHalf && (this.#lastLoaderState !== 'fixed' || forceCalculate)) {
        this.#lastLoaderState = 'fixed';
        this.#elementPartials.loadMoreSpinner.style.top = '';
        this.#elementPartials.loadMoreSpinner.classList.add('car-search-main__loader-holder--fixed');
      }

      // Last card has crossed center of screen --> bottom
      if (firstCardOverHalf && lastCardOverHalf && (this.#lastLoaderState !== 'bottom' || forceCalculate)) {
        this.#lastLoaderState = 'bottom';
        this.#elementPartials.loadMoreSpinner.classList.remove('car-search-main__loader-holder--fixed');
        this.#elementPartials.loadMoreSpinner.style.top = `${this.#firstAndLastCardElements.last.offsetTop +
          (this.#firstAndLastCardElements.last.scrollHeight / 2) -
          (this.#elementPartials.loadMoreSpinner.scrollHeight / 2)}px`;
      }
    }
  }

  /**
   * Handles results injecting to cars grid if hash #|_s and sessionStorage exists
   *
   * @private
   */
  #handleResultsFromSessionStorage() {
    const sessionStorageData = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    const lastPagingStorageData = window.sessionStorage.getItem(LAST_PAGING_SESSION_STORAGE_KEY);

    // When coming back from car page (back button pressed in browser)
    // URL contains this #|_s (LS = LocalStorage) hash.
    // When that hash exists, we will load car search results from
    // local storage straight to the dom. User won't need to
    // scroll down again
    if (sessionStorageData && window.location.hash === '#|_s') {
      this.#sessionStorageLoaded = true;
      this.#elementPartials.carsGrid.innerHTML = sessionStorageData;
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      history.replaceState(null, '', ' '); // Remove #|_s completely

      if(lastPagingStorageData) {
        this.#currentPage = +lastPagingStorageData;
        window.sessionStorage.removeItem(LAST_PAGING_SESSION_STORAGE_KEY);

        // Show load more button if current page is over
        // maximum pages to load with infinite scroll
        if (this.#currentPage >= this.#maxInfiniteLoadPages) {
          this.#elementPartials.loadMore.classList.add('car-search-main__load-more-button--in');
        }
      }
    }
  }
  /**
   * Updates URL
   *
   * @param {string} searchParamsString  - Same as sent to ajax
   * @param {object[]} selectedFilters - Selected filters
   */
  #updateURL(searchParamsString, selectedFilters) {
    // Generate URL via
    const searchParametersUrl = `${window.baseUrl}?${searchParamsString.toString()}`;
    history.pushState(selectedFilters, '', searchParametersUrl);
  }

  /**
   * Handle popstate when user hits browser's back or forward button
   *
   * @private
   * @param {Event} event event object
   */
  async #handlePopState(event) {
    // Check that URL does not contain hash
    if (window.location.hash === '' && !this.#sessionStorageLoaded && Object.keys(event.state).length) {
      this.toggleLoadingAnimation = true;

      // Set sessionStorageLoaded to its initial value
      this.#sessionStorageLoaded = false;
      this.searchCars({ doNotSaveHistory: true });
    }
  }

  #handleNoMoreResultsLeft() {
    if (this.#currentCarsCount <= this.#visibleCarsCount) {
      this.#elementPartials.noMoreResults.classList.add('no-more-results--in');

      // Hide load more if exists
      this.#elementPartials.loadMore.classList.remove('car-search-main__load-more-button--in');
    } else {
      this.#elementPartials.noMoreResults.classList.remove('no-more-results--in');
    }
  }

  /**
   * Load more button click event
   *
   * @param {Event} event - Binded event
   */
  #loadMore(event) {
    event.preventDefault();
    if (this.#currentPage === this.#maxInfiniteLoadPages) {
      this.#elementPartials.loadMore.classList.add('car-search-main__load-more-button--in');
    }
    this.#currentPage += 1;
    this.searchCars({ loadMore: true, doNotSaveHistory: true });
  }

  /**
   * Observer for load more button when user
   * scrolls button to viewport, execute load more
   */
  #observeLoadMoreButton() {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.#currentPage <= this.#maxInfiniteLoadPages && this.#currentCarsCount > this.#visibleCarsCount) {
          this.#elementPartials.loadMore.click();
        }
      });
    }, { threshold: 1 });

    observer.observe(this.#elementPartials.loadMore);
  }

  /**
   * Set loader initial top value
   *  - Get first card on dom
   *  - Get bounding client rect top value
   *  - Get car scrollHeight / 2
   *  - Set loader top value: bouningClientRect.top + scrollHeight / 2
   */
  #setLoaderInitialTopValue() {
    const firstCard = this.#firstAndLastCardElements.first;
    this.#elementPartials.loadMoreSpinner.style.top = `${(firstCard.scrollHeight / 2) + firstCard.offsetTop - (this.#elementPartials.loadMoreSpinner.scrollHeight / 2)}px`;
  }

  /**
   * Set first and last car cards top- and scrollHeight -value
   *
   * @private
   */
  #setFirstAndLastCardsPositionInfo() {
    const [firstCard, lastCard] = this.#elementPartials.carsGrid.querySelectorAll('.card:first-child, .card:last-child');

    // If first or last card does not exists, fallback to carsGrid
    // to avoid javascript error when calculating spinner position
    if (!firstCard || !lastCard) {
      this.#firstAndLastCardElements.first = this.#elementPartials.carsGrid;
      this.#firstAndLastCardElements.last = this.#elementPartials.carsGrid;
    } else {
      this.#firstAndLastCardElements.first = firstCard;
      this.#firstAndLastCardElements.last = lastCard;
    }
  }

  /**
   * init Static initializer of the class.
   *
   * @static
   */
  static init() {
    const carSearchIncoming = document.querySelector('.car-search-incoming-2023');

    if (carSearchIncoming) {
      new CarSearchIncoming(carSearchIncoming);
    }
  }
}

export default CarSearchIncoming;
